import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { userAPI } from "../../services/api";
import { LEAD_STAGE, automationNodeLeadStageToRouteMap } from "../../Utils/constants";
import useLeadStage from "../../hooks/useLeadStage";
import Toast from "../../components/Toast";
import { motion } from "framer-motion";
import SanctionCompletionModal from "../../components/SanctionCompletedModal.jsx";
import FlowFooter from "../../components/FlowFooter";

const curr_page_lead_stage = [LEAD_STAGE.SANCTION_COMPLETED];

export default function Disbursal2() {
  const navigate = useNavigate();
  const { leadStage, isLoadingStage } = useLeadStage();
  const [accountPart, setAccountPart] = useState("");
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "warning",
  });
  const [accountData, setAccountData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDisbursing, setIsDisbursing] = useState(false);
  const [disbursalError, setDisbursalError] = useState(null);
  const [showSanctionModal, setShowSanctionModal] = useState(true);

  // const [disbursalAmount, setDisbursalAmount] = useState(null);

  const showToast = (message, type = "warning") => {
    setToast({ show: true, message, type });
  };

  const hideToast = () => {
    setToast({ show: false, message: "", type: "warning" });
  };

  // Fire Meta Pixel conversion (snappaisa_conv) on disbursal page load
  useEffect(() => {
    if (typeof window !== "undefined" && window.fbq) {
      window.fbq("track", "snappaisa_conv");
    }
  }, []);

  // Fire Google Ads conversion (squidloan_2026 conversion page)
  useEffect(() => {
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("event", "conversion", { send_to: "AW-17893337606/lxxnCOCvoPEbEIbUmtRC" });
    }
  }, []);

  // Fire Google Ads conversion for LUNEX_google_search (Snappaisa_conv conversion page)
  useEffect(() => {
    // Check if conversion has already been fired
    const conversionFired = localStorage.getItem("snappaisa_conv_fired");
    if (conversionFired === "true") {
      return; // Don't fire again if already fired
    }

    // Get utm_source from URL params or sessionStorage
    const urlParams = new URLSearchParams(window.location.search);
    let utm_source = urlParams.get("utm_source");

    // If not in URL, check sessionStorage
    if (!utm_source) {
      const storedUtmParams = sessionStorage.getItem("utmParams");
      if (storedUtmParams) {
        try {
          const parsed = JSON.parse(storedUtmParams);
          utm_source = parsed.utm_source;
        } catch (e) {
          console.error("Error parsing UTM params from sessionStorage:", e);
        }
      }
    }

    // Fire conversion only if utm_source is LUNEX_google_search
    if (utm_source === "Lunex_Google" && typeof window !== "undefined" && window.gtag) {
      window.gtag("event", "conversion", { send_to: "AW-17895773186/PfEICI3s1_MbEIKor9VC" });
      // Set flag to prevent firing again
      localStorage.setItem("snappaisa_conv_fired", "true");
    }
  }, []);

  // Check routing after journey is loaded
  useEffect(() => {
    if (isLoadingStage) {
      return;
    }

    if (leadStage && !curr_page_lead_stage.includes(leadStage)) {
      navigate(automationNodeLeadStageToRouteMap[leadStage]);
    }
  }, [leadStage, navigate, isLoadingStage]);

  useEffect(() => {
    fetchAccountNumber();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAccountNumber = async () => {
    try {
      setIsLoading(true);
      const response = await userAPI.getAccountNumber();
      setAccountData(response);
    } catch (error) {
      showToast(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAccountChange = (e) => {
    // Only allow numbers
    let value = e.target.value.replace(/[^0-9]/g, "");
    setAccountPart(value);
  };

  // Get last 4 digits from account number
  const getLastFourDigits = () => {
    if (accountData?.accountNo) {
      return accountData.accountNo.slice(-4);
    }
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setDisbursalError(null);

    if (accountData?.isAccountNoMasked) {
      const lastFour = getLastFourDigits();
      if (!accountPart.length) {
        showToast("Please enter the account number");
        return;
      }

      if (!accountPart.endsWith(lastFour)) {
        showToast("Account number must end with " + lastFour);
        return;
      }

      try {
        setIsDisbursing(true);
        await userAPI.verifyPennyDrop({
          accountNumber: accountPart,
          ifscCode: accountData?.ifsc,
          beneficiaryName: accountData?.beneficiaryName,
        });
        await fetchAccountNumber();
        setAccountPart("");
        showToast("Account verified successfully", "success");
      } catch (error) {
        showToast(error.message || "Failed to verify account", "error");
      } finally {
        setIsDisbursing(false);
      }
      return;
    }

    const accountNumber = accountData?.accountNo;

    if (!accountNumber) {
      showToast("Account number not available");
      return;
    }

    try {
      setIsDisbursing(true);
      const data = {
        bank_acc_no: accountNumber,
      };
      const response = await userAPI.disbursed(data);
      if (response.status === 200) {
        navigate("/dashboard");
      }
    } catch (error) {
      setDisbursalError(error.message);
    } finally {
      setIsDisbursing(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] via-[#FFFFFF] to-[#F1F5F9] text-[#1E293B] relative flex flex-col">
      <SanctionCompletionModal show={showSanctionModal} handleSanctionModalClose={() => setShowSanctionModal(false)} />
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="hidden sm:block absolute -top-20 -right-10 w-80 h-80 bg-[#FF6B35]/10 blur-[120px]"></div>
        <div className="hidden sm:block absolute top-32 -left-10 w-72 h-72 bg-[#1E3A8A]/10 blur-[110px]"></div>
        <div className="hidden sm:block absolute bottom-0 right-1/3 w-96 h-96 bg-[#FF6B35]/5 blur-[130px]"></div>
      </div>

      <div className={`relative flex-1 flex flex-col pt-20 sm:pt-24 md:pt-28 pb-6 sm:pb-10 px-3 sm:px-4 md:px-6 lg:px-10 mt-0 overflow-y-auto ${showSanctionModal ? "blur-md pointer-events-none select-none" : ""}`}>
        <div className="max-w-xl w-full mx-auto flex items-center justify-center min-h-[60vh] space-y-4 sm:space-y-6 py-2 sm:py-4">
          <div className="bg-white border border-[#E2E8F0] rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 shadow-2xl shadow-[#FF6B35]/10 w-full">
            {isLoading ? (
              <div className="text-center py-6 sm:py-8">
                <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-t-2 border-b-2 border-[#FF6B35] mx-auto mb-4"></div>
                <p className="text-sm sm:text-base text-[#64748B]">Loading...</p>
              </div>
            ) : accountData?.isAccountNoMasked ? (
              <form className="w-full" onSubmit={handleSubmit}>
                <div className="mb-4 sm:mb-6">
                  <label className="block text-base sm:text-lg font-semibold text-[#1E3A8A] text-center mb-3 sm:mb-4">
                    Please enter the Bank Account no:
                  </label>
                  <p className="text-xs sm:text-sm text-[#64748B] text-center mb-3">
                    Enter the account number ending with <b className="text-[#FF6B35]">{getLastFourDigits()}</b>
                  </p>
                  <div className="flex flex-col gap-3 sm:gap-4 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl sm:rounded-2xl px-3 sm:px-4 py-4 sm:py-5 w-full">
                    <div className="flex flex-col gap-2 text-left text-xs sm:text-sm text-[#64748B]">
                      <span className="font-semibold text-[#FF6B35] uppercase tracking-wider">Bank details on file</span>
                      <div className="flex flex-col gap-1 text-xs sm:text-sm">
                        <div className="flex justify-between gap-2">
                          <span className="text-[#94A3B8]">Beneficiary</span>
                          <span className="font-medium text-[#1E293B]">{accountData?.beneficiaryName || "—"}</span>
                        </div>
                        <div className="flex justify-between gap-2">
                          <span className="text-[#94A3B8]">IFSC</span>
                          <span className="font-mono text-[#1E293B]">{accountData?.ifsc || "—"}</span>
                        </div>
                      </div>
                    </div>
                    <input
                      type="number"
                      inputMode="numeric"
                      value={accountPart}
                      onChange={handleAccountChange}
                      placeholder="Enter account no."
                      maxLength={19}
                      className="w-full px-3 sm:px-4 py-2.5 sm:py-3.5 rounded-xl sm:rounded-2xl bg-white border border-[#E2E8F0] text-sm sm:text-base text-[#1E293B] placeholder:text-[#94A3B8] text-center font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/40 focus:border-[#FF6B35] transition-all"
                    />
                  </div>
                </div>
                {!disbursalError && (
                  <button
                    type="submit"
                    disabled={isDisbursing}
                    className="w-full py-3 sm:py-3.5 rounded-xl sm:rounded-2xl bg-gradient-to-r from-[#FF6B35] to-[#ff8c42] text-white text-sm sm:text-base font-semibold hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                  >
                    {isDisbursing && <span className="inline-block h-4 w-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />}
                    {isDisbursing ? "Verifying..." : "Verify Account"}
                  </button>
                )}
              </form>
            ) : (
              <div className="text-center">
                <div className="mb-4 sm:mb-6">
                  <h2 className="text-lg sm:text-xl font-bold text-[#1E3A8A] mb-3 sm:mb-4">Ready for Disbursal!</h2>
                  <p className="text-xs sm:text-sm text-[#64748B] mb-4 sm:mb-6">Your account details are already verified.</p>
                </div>
                {!disbursalError && (
                  <button
                    onClick={handleSubmit}
                    disabled={isDisbursing}
                    className="w-full py-3 sm:py-3.5 rounded-xl sm:rounded-2xl bg-gradient-to-r from-[#FF6B35] to-[#ff8c42] text-white text-sm sm:text-base font-semibold hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
                  >
                    {isDisbursing ? "Disbursing..." : "Proceed to disbursal!!!"}
                  </button>
                )}
              </div>
            )}

            {disbursalError && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ type: "spring", stiffness: 300, damping: 25 }}
                className="mt-4 w-full bg-red-50 border border-red-200 rounded-xl sm:rounded-2xl p-3 sm:p-4"
              >
                <p className="text-xs sm:text-sm text-red-700 text-center font-medium">{disbursalError}</p>
              </motion.div>
            )}
          </div>
        </div>
      </div>
      {toast.show && (
        <Toast show={toast.show} message={toast.message} type={toast.type} onClose={hideToast} />
      )}
      <FlowFooter />
    </div>
  );
}
